import { useState, useEffect, useCallback, useMemo } from 'react';
import { RoadmapProject } from '../types';
import { AsanaTask, getAsanaTasks } from '../../../services/asanaService';
import { getOrganizationData } from '../../../services/organizationS3Service';

interface UseRoadmapDataProps {
  orgId?: string;
  onProjectsLoaded?: (count: number) => void;
  /** When provided, skips internal fetch and uses these tasks directly. */
  externalTasks?: AsanaTask[];
}

/** Converts an AsanaTask array into RoadmapProject array. All tasks are included;
 *  those without a launch_date will fall into the LATER horizon bucket. */
const tasksToRoadmapProjects = (tasks: AsanaTask[]): RoadmapProject[] => {
  // ── Debug: log launch_date values so we can diagnose quarter issues ──
  const sample = tasks.slice(0, 5);
  console.group('[Roadmap] tasksToRoadmapProjects — launch_date sample');
  sample.forEach(t =>
    console.log(
      `  gid=${t.gid} | name="${t.name}" | launch_date=${JSON.stringify((t as any).launch_date)} | program=${t.program}`
    )
  );
  const withDate = tasks.filter(t => (t as any).launch_date);
  const withoutDate = tasks.filter(t => !(t as any).launch_date);
  console.log(`  Total: ${tasks.length} | with launch_date: ${withDate.length} | without: ${withoutDate.length}`);
  console.groupEnd();

  return tasks
    .map(task => ({
      gid: task.gid,
      name: task.name,
      program: task.program || 'Unassigned',
      status: (task as any).status || 'Not Started',
      launch_date: task.launch_date!,
      permalink_url: task.permalink_url || '',
      key_deliverables: (task as any).key_deliverables || '',
      op_decision: (task as any).op_decision || '',
      mission_fit: (task as any).mission_fit || '',
      annualized_entitlement: parseFloat((task as any).annualized_entitlement) || 0,
      hc_sde: parseFloat((task as any).hc_sde) || 0,
      l7_manager: (task as any).l7_sdm || (task as any).l7_manager || '',
      assignee: task.assignee,
      created_at: task.created_at,
      problem_statement: task.problem_statement || '',
      desired_outcome: task.desired_outcome || '',
      business_impact: task.business_impact || '',
      theme: task.theme || '',
      target_year: (task as any).target_year || '',
      request_type: (task as any).request_type || '',
    }));
};

export const useRoadmapData = ({ orgId, onProjectsLoaded, externalTasks }: UseRoadmapDataProps) => {
  const [fetchedProjects, setFetchedProjects] = useState<RoadmapProject[]>([]);
  const [isLoading, setIsLoading] = useState(!externalTasks); // skip loading if external tasks provided
  const [error, setError] = useState<string | null>(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const [resolvedProjectId, setResolvedProjectId] = useState<string | undefined>(undefined);

  // When external tasks are provided, derive projects from them directly (no fetch needed)
  const externalProjects = useMemo(
    () => (externalTasks ? tasksToRoadmapProjects(externalTasks) : null),
    [externalTasks]
  );

  // Notify parent when external projects are derived
  useEffect(() => {
    if (externalProjects !== null && onProjectsLoaded) {
      onProjectsLoaded(externalProjects.length);
    }
  }, [externalProjects, onProjectsLoaded]);

  const loadProjects = useCallback(async (forceRefresh: boolean = false) => {
    // If external tasks are provided, don't fetch — parent owns the data
    if (externalTasks) return;

    try {
      if (forceRefresh) {
        setIsSyncing(true);
      } else {
        setIsLoading(true);
      }

      setError(null);

      // Resolve the Asana project ID from the org ID
      // Checks portfolios first (new model), then falls back to org.asanaProject (legacy).
      let asanaProjectId: string | undefined = orgId;
      if (orgId) {
        // Force refresh to ensure we have org data on direct URL navigation (cold start)
        const orgData = await getOrganizationData(true, 'StakeholderRoadmap');
        const org = orgData?.organizations.find(
          o => o.id === orgId || o.asanaProject === orgId || o.name === orgId
        );
        if (org) {
          // Check portfolios first for asanaProjectId (new multi-tenant model)
          const portfolios = (org as any).portfolios as any[] | undefined;
          if (portfolios && portfolios.length > 0) {
            // Check if orgId matches a specific portfolio's asanaProjectId
            const matchingPortfolio = portfolios.find((p: any) => p.asanaProjectId === orgId);
            if (matchingPortfolio) {
              asanaProjectId = matchingPortfolio.asanaProjectId;
            } else {
              // Default to first portfolio's asanaProjectId
              asanaProjectId = portfolios[0].asanaProjectId;
            }
          } else if (org.asanaProject) {
            // Legacy: use org-level asanaProject
            asanaProjectId = org.asanaProject;
          } else {
            setError(`No Asana project configured for organization "${org.name}". Please create a portfolio with an Asana Project ID.`);
            setIsLoading(false);
            setIsSyncing(false);
            return;
          }
        } else {
          // Also search within portfolios across all orgs
          let found = false;
          if (orgData?.organizations) {
            for (const o of orgData.organizations) {
              const portfolios = (o as any).portfolios as any[] | undefined;
              if (portfolios) {
                const matchingPortfolio = portfolios.find((p: any) => p.id === orgId || p.asanaProjectId === orgId);
                if (matchingPortfolio) {
                  asanaProjectId = matchingPortfolio.asanaProjectId;
                  found = true;
                  break;
                }
              }
            }
          }
          if (!found && !asanaProjectId) {
            setError(`No Asana project configured for organization "${orgId}"`);
            setIsLoading(false);
            setIsSyncing(false);
            return;
          }
        }
        // If org not found but orgId is provided, fall through and use orgId directly
        // (it may already be a valid Asana project ID)
      }

      setResolvedProjectId(asanaProjectId);
      const tasks = await getAsanaTasks(forceRefresh, asanaProjectId);
      const roadmapProjects = tasksToRoadmapProjects(tasks);

      setFetchedProjects(roadmapProjects);

      if (onProjectsLoaded) {
        onProjectsLoaded(roadmapProjects.length);
      }
    } catch (err) {
      console.error('Failed to load roadmap projects:', err);
      setError(err instanceof Error ? err.message : 'Failed to load projects');
    } finally {
      setIsLoading(false);
      setIsSyncing(false);
    }
  }, [orgId, onProjectsLoaded, externalTasks]);

  // Initial load — only runs when no external tasks are provided
  useEffect(() => {
    if (!externalTasks) {
      loadProjects(false);
    }
  }, [loadProjects, externalTasks]);

  const handleSync = useCallback(() => {
    if (!externalTasks) {
      loadProjects(true);
    }
    // When embedded, sync is handled by the parent (TaskPrioritization's own sync button)
  }, [loadProjects, externalTasks]);

  const projects = externalProjects ?? fetchedProjects;

  return {
    projects,
    isLoading,
    error,
    isSyncing,
    handleSync,
    /** The resolved Asana project ID (available after first load) */
    asanaProjectId: resolvedProjectId,
  };
};